import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { isEmpty, chain, keyBy } from 'lodash';
import { Button } from '@fiverr-private/button';
import { I18n } from '@fiverr-private/i18n-react';
import { useBreakpoints } from '@fiverr-private/hooks';
import { GigServiceCardListings } from '@fiverr-private/listings';
import { Text } from '@fiverr-private/typography';
import { Stack } from '@fiverr-private/layout_components';
import { HourlyOrderCard, useHydrationState, SafeWrapper } from '@fiverr-private/seller_pages_toolbox';
import { guardWithActivation, PhoneVerificationVariant } from '@fiverr-private/code_activation_triggers';
import { useMonitoring, PAGE_NAME, SOURCES } from '../../../monitoring/index';
import { useFreelancerPageContext } from '../../FreelancerPageContext';
import { isBrowser, UrlContext } from '../../../utils';
import { useLocalizationContext } from '../../../contexts/LocalizationContext';
import { ContactButton } from '../../common/ActionButtons';
import { isPendingUser } from '../../common/ActionButtons/ContactButton/utils/isPendingUser';
import { useRelevantGigs } from '../../../hooks/useRelevantGigs';
import { useRolloutContext } from '../../../contexts/RolloutContext';

const DEFAULT_COUNT_ON_PAGE = 6;
const GIGS_FOR_LARGE_COUNT = 2;
const AI_BRIEF_GENERATION_TEXT = 'Can we work on hourly basis on ';

/**
 * @param {import('../../../types').Gig[]} gigs
 * @returns {import('../../../types').Gig[]}
 */
const orderByPriceAndPro = (gigs) =>
    chain(gigs)
        .orderBy((gig) => gig.price_i, 'desc')
        .orderBy({ is_pro: true }, 'desc')
        .value();

/**
 * @param {import('../../../types').Gig[]} gigs
 * @param {number[]} relevantGigsOrder
 * @returns {import('../../../types').Gig[]}
 */
const orderGigs = (gigs, relevantGigsOrder = []) => {
    const relevantGigs = chain(gigs)
        .filter((gig) => relevantGigsOrder.includes(gig.gig_id))
        .orderBy((gig) => relevantGigsOrder.indexOf(gig.gig_id), 'asc')
        .value();

    const commonGigs = gigs.filter((gig) => !relevantGigsOrder.includes(gig.gig_id));

    return [...relevantGigs, ...orderByPriceAndPro(commonGigs)];
};

const getInitialCountOnPage = (isLoggedIn, isLG) => {
    // -1 -- for cell for custom order card
    // -2 -- count of redundant cell between 2 and 3 columns grid (4 items and 6 items respectively)
    const count = isLoggedIn ? DEFAULT_COUNT_ON_PAGE - 1 : DEFAULT_COUNT_ON_PAGE;
    return isLG ? count : count - 2;
};

/** @type {React.FC<{className?: string; gigs: import('../../../types').Gig[]; showTranslatedUGC?: boolean}>} */
const GigServices = ({ className, gigs, showTranslatedUGC }) => {
    const { username, isLoggedIn, customOrdersAllowed, isCurrentUser, isActivationCompleted, seller } =
        useFreelancerPageContext();
    const { currency } = useLocalizationContext();
    const { fireEvents, Providers } = useMonitoring();
    const { sm: isSM, md: isMD, lg: isLG } = useBreakpoints();
    const { componentKey } = useHydrationState('GigServices');
    const [showAllGigs, setShowAllGigs] = useState(false);
    const isHourlyRateAvailable = !!seller?.hourlyRate;
    const { inCobrasOpenHourlyToMpRollout } = useRolloutContext();

    const withCustomOrder = useMemo(() => {
        if (isCurrentUser) {
            return false;
        } else if (customOrdersAllowed) {
            return (isLoggedIn || isHourlyRateAvailable) && isBrowser();
        }
        return false;
    }, [isCurrentUser, customOrdersAllowed, isLoggedIn]);

    const contextualGigs = useRelevantGigs(gigs);

    const relevantGigsMap = useMemo(() => keyBy(contextualGigs, (gig) => gig.gig_id), [contextualGigs]);

    const { initialCountOnPage, withBigGridCell } = useMemo(() => {
        const initialCountOnPage = getInitialCountOnPage(withCustomOrder, isLG);
        const bigGridCellBreakpoint = initialCountOnPage - GIGS_FOR_LARGE_COUNT;
        const withBigGridCell =
            !isEmpty(relevantGigsMap) &&
            (isLG ? gigs.length >= bigGridCellBreakpoint : gigs.length > bigGridCellBreakpoint);
        return {
            initialCountOnPage: withBigGridCell ? initialCountOnPage - 1 : initialCountOnPage,
            withBigGridCell,
        };
    }, [withCustomOrder, isLG, gigs, relevantGigsMap]);

    const orderedGigs = useMemo(() => {
        const relevantGigsOrder = contextualGigs.map((item) => item.gig_id);
        return showAllGigs
            ? orderGigs(gigs, relevantGigsOrder)
            : orderGigs(gigs, relevantGigsOrder).slice(0, initialCountOnPage);
    }, [gigs, showAllGigs, initialCountOnPage, contextualGigs]);

    const onViewAllBI = () => {
        fireEvents({
            [Providers.BigQuery]: {
                type: 'clicked_on_show_more_services_business_seller_page',
            },
            [Providers.Mixpanel]: {
                type: 'click see all services',
            },
        });
    };

    const onViewAll = useCallback(() => {
        onViewAllBI();
        setShowAllGigs(true);
    }, []);

    const showAllButton = showAllGigs ? false : gigs.length > initialCountOnPage;
    const showGridGigs = isBrowser() ? isMD : true;
    const fiverrChoice = UrlContext.isFiverrChoice();
    const relevantGigIds = Object.keys(relevantGigsMap);
    const layout = showGridGigs ? GigServiceCardListings.LAYOUT.GRID : GigServiceCardListings.LAYOUT.LIST_HORIZONTAL;
    const T_PREFIX = 'freelancer_page.gig_services';

    useEffect(() => {
        if (!showGridGigs) {
            setShowAllGigs(true);
        }
    }, [showGridGigs]);

    if (isEmpty(gigs)) {
        return null;
    }

    const openUserActivateModal = async () => {
        await guardWithActivation({
            sourceComponent: `${PAGE_NAME}_custom_order`,
            phoneVerificationConfig: { variant: PhoneVerificationVariant.CONTACT },
        });
    };

    const showCustomOrderCard = withCustomOrder && !isHourlyRateAvailable;
    const showHourlyOrderCard = withCustomOrder && isHourlyRateAvailable;

    return (
        <div className={className} key={componentKey}>
            <Stack direction="row" justifyContent="spaceBetween" marginBottom="6" alignItems="center">
                <Text size="b_xxl" fontWeight="bold">
                    {isSM ? <I18n k={`${T_PREFIX}.title`} /> : <I18n k={`${T_PREFIX}.short_title`} />}
                </Text>
                {showAllButton && (
                    <Button className="view-all-btn" variant="outline" onClick={onViewAll} intent="secondary">
                        <I18n k={`${T_PREFIX}.view_all`} params={{ count: gigs.length }} />
                    </Button>
                )}
            </Stack>
            <GigServiceCardListings
                openInNewTab
                isUserPending={isPendingUser(isActivationCompleted)}
                openUserActivateModal={openUserActivateModal}
                showTranslatedUGC={showTranslatedUGC}
                source={PAGE_NAME}
                currency={currency}
                items={orderedGigs}
                relevantGigIds={relevantGigIds}
                layout={layout}
                fiverrChoice={fiverrChoice}
                sellerUsername={username}
                sellerDisplayName={seller?.user?.profile?.displayName || username}
                withBigGridCell={withBigGridCell}
                withCustomOrder={showCustomOrderCard}
                customOrderProps={{
                    requestButton: (
                        <ContactButton
                            aiBriefOnly
                            username={username}
                            source={SOURCES.gigsCustomOffer}
                            contactButtonProps={{
                                size: 'sm',
                                variant: 'outline',
                                intent: 'secondary',
                            }}
                            buttonCopy={<I18n k={'freelancer_page.gig_services.custom_order_ai_brief_button'} />}
                        />
                    ),
                }}
                renderCustomCard={
                    showHourlyOrderCard
                        ? () => (
                              <ContactButton
                                  aiBriefOnly
                                  fullWidth
                                  fullHeight
                                  username={username}
                                  disableProMigration={inCobrasOpenHourlyToMpRollout}
                                  source={SOURCES.gigsCustomOffer}
                                  aiBriefInitialGenerationText={AI_BRIEF_GENERATION_TEXT}
                                  renderTrigger={() => (
                                      <HourlyOrderCard
                                          hourlyRate={seller?.hourlyRate.priceInCents / 100}
                                          sellerName={seller?.user.profile.displayName}
                                      />
                                  )}
                              />
                          )
                        : null
                }
            />
        </div>
    );
};

/** @type {typeof GigServices} */
const SafeGigServices = SafeWrapper(GigServices, 'GigServices');

export { SafeGigServices as GigServices };
